import { api } from '@/lib/api';
import type {
  Bill, BillsListResponse, BillsListFilters,
  BillDetail, BillDocument, BillPayment,
} from './types';

interface V4Bill {
  id: string;
  quickbooksBillId: string;
  accountId: string | null;
  vendorQuickbooksId: string | null;
  vendorName: string | null;
  docNumber: string | null;
  txnDate: string | null;
  dueDate: string | null;
  totalAmt: string | number;
  balance: string | number;
  currency: string;
  status: 'open' | 'paid' | 'overdue' | 'void';
  privateNote: string | null;
  memo: string | null;
  lastSyncedAt: string | null;
  lastPaymentDate?: string | null;
  lastPaymentMethod?: string | null;
  lastPaymentAccountName?: string | null;
  lastPaymentAmount?: string | number | null;
  lastBillPaymentQbId?: string | null;
  paymentProofDmsId?: string | null;
  paymentProofGdriveUrl?: string | null;
}

interface V4BillsResponse {
  data: V4Bill[];
  pagination?: { pageNumber: number; pageLength: number; total: number };
}

function transformBill(v4: V4Bill): Bill {
  return {
    id: v4.id,
    quickbooks_bill_id: v4.quickbooksBillId,
    account_id: v4.accountId ?? '',
    vendor_quickbooks_id: v4.vendorQuickbooksId ?? null,
    vendor_name: v4.vendorName ?? null,
    doc_number: v4.docNumber ?? null,
    txn_date: v4.txnDate ?? null,
    due_date: v4.dueDate ?? null,
    total_amt: v4.totalAmt,
    balance: v4.balance,
    currency: v4.currency,
    status: v4.status,
    private_note: v4.privateNote ?? null,
    memo: v4.memo ?? null,
    last_synced_at: v4.lastSyncedAt ?? null,
    last_payment_date: v4.lastPaymentDate ?? null,
    last_payment_method: v4.lastPaymentMethod ?? null,
    last_payment_account_name: v4.lastPaymentAccountName ?? null,
    last_payment_amount: v4.lastPaymentAmount ?? null,
    last_bill_payment_qb_id: v4.lastBillPaymentQbId ?? null,
    payment_proof_dms_id: v4.paymentProofDmsId ?? null,
    payment_proof_gdrive_url: v4.paymentProofGdriveUrl ?? null,
  };
}

export async function fetchBills(filters: BillsListFilters): Promise<BillsListResponse> {
  const v4 = await api.get<V4BillsResponse>('/v4/bills', { ...filters });
  return {
    data: (v4.data ?? []).map(transformBill),
    pagination: v4.pagination,
  };
}

interface V4BillDocument {
  id: string;
  billId: string;
  role: string;
  dmsDocumentId: string | null;
  gdriveFileId: string | null;
  gdriveUrl: string | null;
  linkedAt: string | null;
  linkedBy: string | null;
  notes: string | null;
}

interface V4BillPayment {
  id: string;
  billId: string;
  quickbooksBillPaymentId: string | null;
  paymentDate: string;
  paymentMethod: string | null;
  paymentAccountQuickbooksId: string | null;
  paymentAccountName: string | null;
  amount: string | number;
  currency: string;
  referenceNumber: string | null;
  paymentProofDmsId: string | null;
  paymentProofGdriveId: string | null;
  paymentProofGdriveUrl: string | null;
  notes: string | null;
}

interface V4BillDetail extends V4Bill {
  documents?: V4BillDocument[];
  payments?: V4BillPayment[];
}

function transformDoc(v4: V4BillDocument): BillDocument {
  return {
    id: v4.id,
    bill_id: v4.billId,
    role: v4.role,
    dms_document_id: v4.dmsDocumentId ?? null,
    gdrive_file_id: v4.gdriveFileId ?? null,
    gdrive_url: v4.gdriveUrl ?? null,
    linked_at: v4.linkedAt ?? null,
    linked_by: v4.linkedBy ?? null,
    notes: v4.notes ?? null,
  };
}

function transformPayment(v4: V4BillPayment): BillPayment {
  return {
    id: v4.id,
    bill_id: v4.billId,
    quickbooks_bill_payment_id: v4.quickbooksBillPaymentId ?? null,
    payment_date: v4.paymentDate,
    payment_method: v4.paymentMethod ?? null,
    payment_account_quickbooks_id: v4.paymentAccountQuickbooksId ?? null,
    payment_account_name: v4.paymentAccountName ?? null,
    amount: v4.amount,
    currency: v4.currency,
    reference_number: v4.referenceNumber ?? null,
    payment_proof_dms_id: v4.paymentProofDmsId ?? null,
    payment_proof_gdrive_id: v4.paymentProofGdriveId ?? null,
    payment_proof_gdrive_url: v4.paymentProofGdriveUrl ?? null,
    notes: v4.notes ?? null,
  };
}

export async function fetchBill(id: string): Promise<BillDetail> {
  const v4 = await api.get<V4BillDetail>(`/v4/bills/${encodeURIComponent(id)}`);
  return {
    ...transformBill(v4),
    documents: (v4.documents ?? []).map(transformDoc),
    payments: (v4.payments ?? []).map(transformPayment),
  };
}

export interface AttachPaymentProofInput {
  dmsDocumentId?: string | null;
  gdriveUrl?: string | null;
  gdriveFileId?: string | null;
  /** Defaults to 'payment_initiation' if no payment row exists. */
  role?: string;
}

/**
 * Attach a payment proof (DMS UUID and/or Drive URL) to a bill.
 * If the bill has a payment row, the proof lands on the latest payment.
 * Otherwise, an acc_bill_documents row is created with the given role.
 */
export async function attachPaymentProof(
  billId: string,
  input: AttachPaymentProofInput,
): Promise<{ mode: 'payment-updated' | 'document-created'; paymentId?: string; documentId?: string }> {
  return api.put(`/v4/bills/${encodeURIComponent(billId)}/payment-proof`, input);
}
